import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Plus, Search, Edit, Trash2, MoreVertical,
  Ticket, Loader2, Save, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn, formatCurrency } from '@utils';
import api from '@services/api';
import type { TicketType, ListResponse } from '@types';

const ticketTypeSchema = z.object({
  event_id: z.string().min(1, 'Event is required'),
  name: z.string().min(2, 'Name is required'),
  description: z.string().optional(),
  price: z.number().min(0, 'Price must be 0 or more'),
  capacity: z.number().int().min(1, 'Capacity must be at least 1'),
  sales_start_at: z.string().optional(),
  sales_end_at: z.string().optional(),
  is_hidden: z.boolean().default(false),
});

type TicketTypeForm = z.infer<typeof ticketTypeSchema>;

export default function TicketTypesPage() {
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<TicketType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const { data: ticketTypes, isLoading } = useQuery({
    queryKey: ['ticket-types', currentPage, searchQuery],
    queryFn: () => api.get<ListResponse<TicketType>>('/ticket-types', {
      page: currentPage,
      search: searchQuery || undefined,
    }),
  });

  const { data: events } = useQuery({
    queryKey: ['events-list'],
    queryFn: () => api.get<{ data: any[] }>('/events', { per_page: 100 }),
  });

  const form = useForm<TicketTypeForm>({
    resolver: zodResolver(ticketTypeSchema),
    defaultValues: {
      is_hidden: false,
      price: 0,
      capacity: 100,
    }
  });

  const createMutation = useMutation({
    mutationFn: (data: TicketTypeForm) => api.post('/ticket-types', data),
    onSuccess: () => {
      toast.success('Ticket type created');
      queryClient.invalidateQueries({ queryKey: ['ticket-types'] });
      setShowModal(false);
      form.reset();
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TicketTypeForm }) =>
      api.put(`/ticket-types/${id}`, data),
    onSuccess: () => {
      toast.success('Ticket type updated');
      queryClient.invalidateQueries({ queryKey: ['ticket-types'] });
      setEditingType(null);
      setShowModal(false);
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/ticket-types/${id}`),
    onSuccess: () => {
      toast.success('Ticket type deleted');
      queryClient.invalidateQueries({ queryKey: ['ticket-types'] });
    },
  });

  const handleSubmit = (data: TicketTypeForm) => {
    if (editingType) {
      updateMutation.mutate({ id: editingType.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (type: TicketType) => {
    setEditingType(type);
    form.reset({
      event_id: String(type.eventId),
      name: type.name,
      description: type.description || '',
      price: Number(type.price),
      capacity: Number(type.capacity),
      sales_start_at: type.salesStartAt ? new Date(type.salesStartAt).toISOString().slice(0, 16) : undefined,
      sales_end_at: type.salesEndAt ? new Date(type.salesEndAt).toISOString().slice(0, 16) : undefined,
      is_hidden: type.isHidden,
    });
    setOpenMenuId(null);
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this ticket type?')) {
      deleteMutation.mutate(id);
    }
    setOpenMenuId(null);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Ticket Types</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage ticket pricing and availability across all events</p>
        </div>
        <button onClick={() => { setEditingType(null); form.reset(); setShowModal(true); }} className="btn-primary">
          <Plus className="w-4 h-4" /> Create Ticket Type
        </button>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search ticket types..."
            className="input pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search Ticket Types"
          />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-500" /></div>
        ) : ticketTypes?.data?.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No ticket types found.</p>
          </div>
        ) : (
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Name</th>
                <th className="table-header-cell">Event</th>
                <th className="table-header-cell">Price</th>
                <th className="table-header-cell">Sold / Capacity</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {ticketTypes?.data?.map((type: any) => (
                <tr key={type.id} className="table-row">
                  <td className="table-cell">
                    <div className="font-medium text-gray-900 dark:text-white">{type.name}</div>
                    {type.description && (
                      <div className="text-xs text-gray-500 truncate max-w-[200px]">{type.description}</div>
                    )}
                  </td>
                  <td className="table-cell text-sm text-gray-600 dark:text-gray-400">
                    {type.event?.name || 'Unknown Event'}
                  </td>
                  <td className="table-cell font-medium">
                    {type.price === 0 ? 'Free' : formatCurrency(type.price)}
                  </td>
                  <td className="table-cell text-sm">
                    <span className="font-medium">{type.soldCount ?? 0}</span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="text-gray-600 dark:text-gray-400">{type.capacity}</span>
                  </td>
                  <td className="table-cell">
                    <span className={cn(
                      "px-2 py-1 rounded-full text-xs font-medium",
                      !type.isHidden ? "bg-success-100 text-success-800" : "bg-gray-100 text-gray-800"
                    )}>
                      {!type.isHidden ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="table-cell text-right relative">
                    <button onClick={() => setOpenMenuId(openMenuId === type.id ? null : type.id)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" aria-label="Actions">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {openMenuId === type.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded shadow-lg border border-gray-200 dark:border-gray-700 z-20 py-1">
                          <button onClick={() => handleEdit(type)} className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm">
                            <Edit className="w-4 h-4" /> Edit
                          </button>
                          <button onClick={() => handleDelete(type.id)} className="w-full text-left px-4 py-2 hover:bg-danger-50 text-danger-600 flex items-center gap-2 text-sm">
                            <Trash2 className="w-4 h-4" /> Delete
                          </button>
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {ticketTypes?.meta && ticketTypes.meta.lastPage > 1 && (
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(ticketTypes.meta.currentPage - 1) * ticketTypes.meta.perPage + 1} to{' '}
            {Math.min(ticketTypes.meta.currentPage * ticketTypes.meta.perPage, ticketTypes.meta.total)} of{' '}
            {ticketTypes.meta.total} ticket types
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="btn-secondary p-2"
              aria-label="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {ticketTypes.meta.lastPage}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(ticketTypes.meta.lastPage, p + 1))}
              disabled={currentPage === ticketTypes.meta.lastPage}
              className="btn-secondary p-2"
              aria-label="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showModal || editingType) && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingType(null); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content max-w-lg"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{editingType ? 'Edit Ticket Type' : 'Create Ticket Type'}</h3>
              <button onClick={() => { setShowModal(false); setEditingType(null); }} aria-label="Close Modal"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="p-6 space-y-4">
              <div>
                <label className="label" htmlFor="event_id">Event</label>
                <select {...form.register('event_id')} id="event_id" className="input" disabled={!!editingType}>
                  <option value="">Select Event</option>
                  {events?.data?.map((e: any) => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
                {form.formState.errors.event_id && <p className="text-danger-600 text-xs mt-1">{form.formState.errors.event_id.message}</p>}
              </div>

              <div>
                <label className="label" htmlFor="name">Name</label>
                <input {...form.register('name')} id="name" className="input" placeholder="e.g. VIP Admission" />
                {form.formState.errors.name && <p className="text-danger-600 text-xs mt-1">{form.formState.errors.name.message}</p>}
              </div>

              <div>
                <label className="label" htmlFor="description">Description</label>
                <input {...form.register('description')} id="description" className="input" placeholder="Optional description" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label" htmlFor="price">Price</label>
                  <input type="number" step="0.01" {...form.register('price', { valueAsNumber: true })} id="price" className="input" />
                </div>
                <div>
                  <label className="label" htmlFor="capacity">Capacity</label>
                  <input type="number" {...form.register('capacity', { valueAsNumber: true })} id="capacity" className="input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label" htmlFor="sales_start_at">Sale Start</label>
                  <input type="datetime-local" {...form.register('sales_start_at')} id="sales_start_at" className="input" />
                </div>
                <div>
                  <label className="label" htmlFor="sales_end_at">Sale End</label>
                  <input type="datetime-local" {...form.register('sales_end_at')} id="sales_end_at" className="input" />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" {...form.register('is_hidden')} id="is_hidden" className="w-4 h-4 rounded" />
                <label htmlFor="is_hidden" className="text-sm text-gray-700 dark:text-gray-300">Hidden (not shown publicly)</label>
              </div>

              <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="btn-primary w-full">
                {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingType ? 'Update Ticket Type' : 'Create Ticket Type'}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
